<template>
  <v-dialog v-model="showCombineLegsForm" width="90%" max-width="1400px" persistent>
    <v-card class="combine-legs-card" elevation="12">
      <!-- Enhanced Header -->
      <v-card-title class="combine-legs-header pa-2">
        <div class="header-content">
          <div class="title-section">
            <span class="text-h6 font-weight-bold">Combine Legs</span>
          </div>

          <div class="route-display mt-3">
            <v-chip class="location-chip" color="white" text-color="primary" label small>
              <v-icon left small>mdi-map-marker</v-icon>
              {{ legDetails.current_leg ? legDetails.current_leg.source_location_name : "" }}
            </v-chip>

            <v-icon class="route-arrow" color="white">mdi-arrow-right</v-icon>

            <v-chip class="location-chip" color="white" text-color="primary" label small>
              <v-icon left small>mdi-map-marker-outline</v-icon>
              {{ legDetails.current_leg ? legDetails.current_leg.mid_location_name : "" }}
            </v-chip>

            <template v-if="legDetails.current_leg && legDetails.current_leg.destination_location_name">
              <v-icon class="route-arrow" color="white">mdi-arrow-right</v-icon>
              <v-chip class="location-chip" color="white" text-color="primary" label small>
                <v-icon left small>mdi-flag-checkered</v-icon>
                {{ legDetails.current_leg.destination_location_name }}
              </v-chip>
            </template>
          </div>
        </div>

        <v-spacer></v-spacer>

        <v-btn icon large @click="showCombineLegsForm = false" class="close-btn">
          <v-icon color="white" size="28">mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Error Alert -->
      <v-card-text v-if="formErrors && formErrors.non_field_errors" class="px-6 pt-0">
        <v-alert dense type="error" border="left" colored-border elevation="2" class="error-alert">
          <div class="d-flex align-center">
            <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
            <span class="font-weight-medium">Please fix the following errors:</span>
          </div>
          <v-list dense class="pa-0 mt-2 transparent">
            <v-list-item v-for="(error, i) in formErrors.non_field_errors" :key="i" dense class="px-0">
              <v-list-item-content>
                <span class="body-2">{{ i + 1 }}. {{ error }}</span>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-alert>
      </v-card-text>

      <!-- Selected Leg Details Section -->
      <v-card-text class="px-6 py-4">
        <div class="section-header mb-4">
          <v-icon color="primary" class="mr-2">mdi-information-outline</v-icon>
          <span class="text-h6 font-weight-bold primary--text">Selected Leg Details</span>
        </div>

        <v-card flat class="details-card pa-4" color="grey lighten-5">
          <v-row>
            <v-col cols="12" sm="6" md="4" lg="2" v-for="detail in legDetailsDisplay" :key="detail.label">
              <div class="detail-item">
                <div class="detail-label">
                  <v-icon small :color="detail.color" class="mr-1" v-if="detail.icon">{{ detail.icon }}</v-icon>
                  <span v-if="detail.label"> {{ detail.label }}</span>
                </div>
                <div class="detail-value">{{ detail.value }}</div>
              </div>
            </v-col>
          </v-row>
        </v-card>
      </v-card-text>

      <!-- Similar Legs Section -->
      <v-card-text class="px-6 py-4">
        <div class="d-flex align-center justify-space-between mb-4">
          <div class="section-header">
            <v-icon color="primary" class="mr-2">mdi-format-list-bulleted</v-icon>
            <span class="text-h6 font-weight-bold primary--text">Similar Legs</span>
            <v-chip small color="info" text-color="white" class="ml-2" v-if="selectedLegs.length">
              {{ selectedLegs.length }} selected
            </v-chip>
          </div>

          <v-text-field label="Search legs..." outlined dense clearable hide-details class="search-field"
            style="max-width: 300px;" prepend-inner-icon="mdi-magnify" v-model="search"
            @input="onFilterTextBoxChanged()"></v-text-field>
        </div>

        <v-card flat class="grid-container" elevation="2">
          <AgGridVue style="width: 100%; height: 350px" class="ag-theme-alpine enhanced-grid" rowSelection="multiple"
            id="combineLegGrid" :columnDefs="headers" :context="context" @grid-ready="onGridReady"
            :defaultColDef="defaultColDef" :grid-options="gridOptions" :rowData="allLegsList"
            @selection-changed="setSelectedRows"></AgGridVue>
        </v-card>
      </v-card-text>

      <!-- Trip Details Form -->
      <v-card-text class="px-6 py-4">
        <div class="section-header mb-4">
          <v-icon color="primary" class="mr-2">mdi-truck</v-icon>
          <span class="text-h6 font-weight-bold primary--text">Trip Configuration</span>
        </div>

        <v-form ref="combineLegAssetForm" v-if="value" v-model="isValid">
          <v-card flat class="form-card pa-4" color="grey lighten-5">
            <v-row>
              <!-- Trip Selection -->
              <v-col cols="12" md="6" lg="3" v-if="availableTrips && availableTrips.length">
                <div class="form-field-wrapper">
                  <MultiSelect :returnObject="true" hide-details="auto" :items="availableTrips" itemText="trip_id"
                    itemValue="id" label="Select Trip" required outlined dense
                    :rules="[(val) => !!val || 'Trip is required']" v-model="selectedTrip"
                    @change="getAllocatedAssets()">
                    <template v-slot:prepend-inner>
                      <v-icon small color="primary">mdi-route</v-icon>
                    </template>
                  </MultiSelect>
                </div>
              </v-col>

              <!-- Date and Time Fields -->
              <v-col cols="12" md="6" lg="3">
                <div class="form-field-wrapper">
                  <DateField ref="TripDateField" :inputProps="{
                    dense: true,
                    outlined: true,
                    label: 'Trip Date *',
                    rules: [(val) => !!val || 'Trip Date is Required!'],
                    'hide-details': 'auto',
                    'error-messages': formErrors.trip_date,
                    'prepend-inner-icon': 'mdi-calendar'
                  }" :min="today" v-model="assetFormDetails.trip_date"
                    @change="delete formErrors.trip_date; refreshFormDetails();" />
                </div>
              </v-col>

              <v-col cols="12" md="6" lg="3">
                <div class="form-field-wrapper">
                  <DateField :key="assetFormDetails.est_start_date" :inputProps="{
                    dense: true,
                    outlined: true,
                    label: 'Est. Start Date*',
                    rules: [(val) => !!val || 'Est. Start Date is Required!'],
                    'hide-details': 'auto',
                    'prepend-inner-icon': 'mdi-calendar-clock'
                  }" required :min="assetFormDetails.trip_date" v-model="assetFormDetails.est_start_date"
                    @change="updateEstEndDate" />
                </div>
              </v-col>

              <v-col cols="12" md="6" lg="3">
                <div class="form-field-wrapper">
                  <InputField :key="assetFormDetails.est_start_time" type="time" label="Est. Start Time *"
                    :rules="[(val) => !!val || 'Est. Start Time is Required!']" required hide-details="auto" outlined
                    dense v-model="assetFormDetails.est_start_time" @input="updateEstEndDate">
                    <template v-slot:prepend-inner>
                      <v-icon small color="primary">mdi-clock-start</v-icon>
                    </template>
                  </InputField>
                </div>
              </v-col>

              <v-col cols="12" md="6" lg="3">
                <div class="form-field-wrapper">
                  <DateField ref="EstEndDateField" :key="assetFormDetails.est_end_date" :inputProps="{
                    dense: true,
                    outlined: true,
                    label: 'Est. End Date *',
                    rules: [(val) => !!val || 'Est. End Date is Required!'],
                    'hide-details': 'auto',
                    'prepend-inner-icon': 'mdi-calendar-check'
                  }" :min="assetFormDetails.est_start_date" required v-model="assetFormDetails.est_end_date" />
                </div>
              </v-col>

              <v-col cols="12" md="6" lg="3">
                <div class="form-field-wrapper">
                  <InputField :key="assetFormDetails.est_end_time" type="time" label="Est. End Time *"
                    :rules="[(val) => !!val || 'Est. End Time is Required!']" required hide-details="auto" outlined
                    dense v-model="assetFormDetails.est_end_time" @input="checkAssetType();">
                    <template v-slot:prepend-inner>
                      <v-icon small color="primary">mdi-clock-end</v-icon>
                    </template>
                  </InputField>
                </div>
              </v-col>

              <!-- Asset Selection -->
              <v-col cols="12" md="6" lg="3">
                <div class="form-field-wrapper">
                  <MultiSelect label="Select Driver *" hide-details="auto" outlined dense :items="driverList"
                    itemText="driver_name" itemValue="id" :disabled="!assetFormDetails.trip_date"
                    :rules="[(val) => !!val || 'Driver is required']" v-model="assetFormDetails.driver"
                    :error-messages="formErrors.driver" @change="vehicleAssigned" :showDriverChips="true">
                    <template v-slot:prepend-inner>
                      <v-icon small color="primary">mdi-account</v-icon>
                    </template>
                  </MultiSelect>
                </div>
              </v-col>

              <v-col cols="12" md="6" lg="3">
                <div class="form-field-wrapper">
                  <MultiSelect label="Select Vehicle *" :items="vehicleList" itemText="inventory_number" itemValue="id"
                    hide-details="auto" outlined dense :disabled="!assetFormDetails.trip_date"
                    :rules="[(val) => !!val || 'Vehicle is required']" v-model="assetFormDetails.vehicle"
                    :error-messages="formErrors.vehicle" @change="delete formErrors.vehicle">
                    <template v-slot:prepend-inner>
                      <v-icon small color="primary">mdi-truck</v-icon>
                    </template>
                  </MultiSelect>
                </div>
              </v-col>

              <v-col cols="12" md="6" lg="3">
                <div class="form-field-wrapper">
                  <MultiSelect label="Select Trailer 1*" :items="trailer1List" hide-details="auto"
                    itemText="inventory_number" itemValue="id" outlined dense :disabled="!assetFormDetails.trip_date"
                    v-model="assetFormDetails.trailer_1" :rules="[(val) => !!val || 'Trailer 1 is required']"
                    :error-messages="formErrors.trailer_1" @change="delete formErrors.trailer_1">
                    <template v-slot:prepend-inner>
                      <v-icon small color="primary">mdi-truck-trailer</v-icon>
                    </template>
                  </MultiSelect>
                </div>
              </v-col>

              <v-col cols="12" md="6" lg="3">
                <div class="form-field-wrapper">
                  <MultiSelect label="Select Trailer 2" :items="trailer2List" itemText="inventory_number" itemValue="id"
                    hide-details="auto" outlined dense :clearable="true"
                    :item-disabled="(item) => item.id == assetFormDetails.trailer_1"
                    :disabled="!assetFormDetails.trip_date || !assetFormDetails.trailer_1"
                    v-model="assetFormDetails.trailer_2" :error-messages="formErrors.trailer_2"
                    @change="delete formErrors.trailer_2">
                    <template v-slot:prepend-inner>
                      <v-icon small color="primary">mdi-truck-trailer</v-icon>
                    </template>
                  </MultiSelect>
                </div>
              </v-col>

              <v-col cols="12" md="6" lg="3">
                <div class="form-field-wrapper">
                  <MultiSelect label="Select Milestones Template *" :items="allMilestoneTemplateList" itemText="name"
                    itemValue="id" :chips="true" hide-details="auto" outlined dense
                    :rules="[(val) => !!val || 'Milestone Template is required']"
                    v-model="assetFormDetails.milestone_template" :error-messages="formErrors.milestone_template"
                    @change="delete formErrors.milestone_template">
                    <template v-slot:prepend-inner>
                      <v-icon small color="primary">mdi-flag-variant</v-icon>
                    </template>
                  </MultiSelect>
                </div>
              </v-col>

              <v-col cols="12" md="6" lg="3">
                <div class="form-field-wrapper">
                  <InputField :key="assetFormDetails.buffer_time" type="number" label="Buffer Time (In Mins)*"
                    :rules="[(val) => !!val || 'Buffer Time is Required!']" required hide-details="auto"
                    v-model="assetFormDetails.buffer_time" />
                </div>
              </v-col>

            </v-row>
          </v-card>
        </v-form>
      </v-card-text>

      <!-- Enhanced Actions -->
      <v-divider></v-divider>
      <v-card-actions class="px-6 py-4 actions-section">
        <v-spacer></v-spacer>
        <v-btn text large class="mr-3" @click="showCombineLegsForm = false">
          <v-icon left>mdi-cancel</v-icon>
          Cancel
        </v-btn>
        <v-btn color="primary" large depressed elevation="2" :disabled="!isValid || !selectedLegs.length"
          :loading="false" @click="submitForm" class="submit-btn">
          Submit

        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import DateField from "@/components/FormBaseComponents/DateField.vue";
import InputField from "@/components/FormBaseComponents/InputField.vue";
import MultiSelect from "@/components/FormBaseComponents/MultiSelect.vue";
import SelectField from "@/components/FormBaseComponents/SelectField.vue";
import FormComponent from "@/components/General/FormComponent.vue";
import { AgGridVue } from "ag-grid-vue";
import moment from "moment";

export default {
  components: {
    DateField,
    InputField,
    SelectField,
    MultiSelect,
    AgGridVue,
    FormComponent,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    legId: {
      type: [Number, String, null],
      default: null,
    },
    companyId: {
      required: true,
    },
    legData: {
      required: true,
      type: Object,
      default: () => { },
    }
  },
  watch: {
    value(val) {
      if (val) {
        this.formErrors = {};
        this.getSimillarLegs();
      } else {
        this.resetValues();
      }
      this.getMilestoneTemplateLists()
    },
  },
  data() {
    return {
      getListCalled: false,
      trailerListTimeout: null,
      driverListTimeout: null,
      vehicleListTimeout: null,
      allMilestoneTemplateList: [],
      today: new Date().toISOString().slice(0, 16),

      search: null,
      allLegsList: [],
      selectedLegs: [],
      selectedLegIds: [],

      formErrors: {},
      selectedTrip: null,
      isValid: true,

      vehicleList: [],
      driverList: [],
      trailer1List: [],
      trailer2List: [],
      trip_date: null,
      allListApi: [],
      routeDuration: null,

      legDetails: {},
      assetFormDetails: {
        buffer_time: 30,
      },
      headers: [
        {
          headerName: "Leg",
          field: "leg_id",
          minWidth: 140,
          checkboxSelection: true,
          headerClass: 'custom-header',
          cellClass: 'font-weight-medium'
        },
        {
          headerName: "Customer",
          field: "customer_name",
          minWidth: 140,
          headerClass: 'custom-header'
        },
        {
          headerName: "DA",
          field: "da_no",
          minWidth: 120,
          headerClass: 'custom-header'
        },
        {
          headerName: "Containers",
          field: "leg_containers",
          minWidth: 180,
          headerClass: 'custom-header',
          valueFormatter: (params) => {
            return params.data.leg_containers
              .map((c) => c.order_container_no)
              .join(", ");
          },
          getQuickFilterText: (params) => {
            if (params.value && params.value.length > 0) {
              return params.value.map((c) => c.order_container_no).join();
            }
          },
        },
        {
          headerName: "Trip",
          field: "leg_trip",
          minWidth: 110,
          headerClass: 'custom-header',
          valueFormatter: (params) => {
            if (params.data.leg_trip.trip_id) {
              return params.data.leg_trip.trip_id;
            } else {
              return "";
            }
          },
          getQuickFilterText: (params) => {
            if (params.value) {
              return params.value.trip_id;
            }
          },
        },
        {
          headerName: "BOE",
          field: "boe",
          minWidth: 110,
          headerClass: 'custom-header'
        },
      ],

      defaultColDef: {
        resizable: true,
        flex: 1,
        sortable: true,
        filter: true,
      },
      gridOptions: {
        onGridSizeChanged: () => {
          this.resizeGrid();
        },
        cacheQuickFilter: true,
        suppressRowClickSelection: true,
        suppressDragLeaveHidesColumns: true,
        enableCellTextSelection: true,
        tooltipShowDelay: 0,
        rowHeight: 45,
        headerHeight: 50,
      },
    };
  },

  computed: {
    showCombineLegsForm: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    context() {
      return { parentComponent: this };
    },
    currentProfile() {
      let obj = localStorage.getItem("userProfile");
      return !obj ? obj : JSON.parse(obj);
    },
    availableTrips() {
      if (
        this.legDetails &&
        this.legDetails.current_leg &&
        this.legDetails.current_leg.leg_trip &&
        Object.keys(this.legDetails.current_leg.leg_trip).length
      ) {
        this.legDetails.current_leg.leg_trip.leg_id =
          this.legDetails.current_leg.id;
      }
      return [
        ...this.selectedLegs.map((leg) => {
          if (Object.keys(leg.leg_trip).length) {
            leg.leg_trip.leg_id = leg.id;
          }
          return leg.leg_trip;
        }),
        this.legDetails.current_leg && this.legDetails.current_leg.leg_trip
          ? this.legDetails.current_leg.leg_trip
          : {},
      ].filter((obj) => Object.keys(obj).length);
    },
    legDetailsDisplay() {
      if (!this.legDetails.current_leg) return [];

      const leg = this.legDetails.current_leg;
      return [
        {
          label: "Leg ID",
          value: leg.leg_id || "",
          icon: "mdi-identifier",
          color: "primary"
        },
        {
          label: "DA",
          value: leg.da_no || "",
          icon: "mdi-file-document",
          color: "info"
        },
        {
          label: "Containers",
          value: leg.leg_containers?.map(c => c.order_container_no).join(", ") || "",
          icon: "mdi-cube-outline",
          color: "success"
        },
        {
          label: "Trip ID",
          value: leg.leg_trip?.trip_id || "",
          icon: "mdi-route",
          color: "warning"
        },
        {
          label: "Transporter",
          value: leg.company_name || "",
          icon: "mdi-truck",
          color: "purple"
        },
        {
          label: "BOE",
          value: leg.boe || "",
          icon: "mdi-clipboard-text",
          color: "teal"
        }
      ];
    }
  },

  methods: {
    getMilestoneTemplateLists(params = {}) {
      this.$bus.$emit("showLoader", true);
      params = {
        limit: "all",
      };
      this.$api.milestoneTemplate
        .getMilestoneTemplateList(params)
        .then((res) => {
          this.allMilestoneTemplateList = res.data;
          this.totalItems = res.count;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },

    onFilterTextBoxChanged() {
      this.gridApi.setQuickFilter(this.search);
    },

    refreshFormDetails() {
      this.formDetails = { ...this.formDetails };
    },

    vehicleAssigned() {
      delete this.formErrors.driver;
      let assignedVehicle = this.driverList.find(
        (dri) => dri.id == this.assetFormDetails.driver
      )?.assigned_vehicle;
      let vehicleExist = this.vehicleList.find(
        (obj) => obj.id == assignedVehicle
      );
      if (vehicleExist) {
        this.assetFormDetails = {
          ...this.assetFormDetails,
          vehicle: assignedVehicle,
        };
      }
    },

    getLists() {

      this.$bus.$emit("showLoader", true);
      return new Promise((resolve, reject) => {
        Promise.all([
          this.getAssetList("Trailer"),
          this.getAssetList("Driver"),
          this.getAssetList("Vehicle"),
        ])
          .then(() => {
            this.getListCalled = false;
            this.$bus.$emit("showLoader", false);
            resolve();
          })
          .catch((err) => {
            this.getListCalled = false;
            this.$bus.$emit("showLoader", false);
            reject();
          });
      });
    },

    updateEstEndDate() {
      const { est_start_date, est_start_time } = this.assetFormDetails;
      const duration = this.legData?.lane_duration || 0;
      const buffer = this.assetFormDetails?.buffer_time || 0;
      const legsCount = (this.selectedLegs.length || 0) + 1;


      if (!est_start_date || !est_start_time) {
        this.assetFormDetails.est_end_date = null;
        this.assetFormDetails.est_end_time = null;
        return;
      }

      const startDateTime = new Date(`${est_start_date}T${est_start_time}`);
      if (isNaN(startDateTime.getTime())) {
        this.assetFormDetails.est_end_date = null;
        this.assetFormDetails.est_end_time = null;
        return;
      }

      const endDateTime = new Date(
        startDateTime.getTime() + (duration + buffer * legsCount * 2) * 60 * 1000
      );
      this.assetFormDetails.buffer_time = 30 * legsCount;

      const pad = (n) => (n < 10 ? "0" + n : n);

      this.$set(
        this.assetFormDetails,
        "est_end_date",
        `${endDateTime.getFullYear()}-${pad(endDateTime.getMonth() + 1)}-${pad(
          endDateTime.getDate()
        )}`
      );
      this.$set(
        this.assetFormDetails,
        "est_end_time",
        `${pad(endDateTime.getHours())}:${pad(endDateTime.getMinutes())}`
      );

      this.$nextTick(() => {
        const endDateField = this.$refs.EstEndDateField;
        if (endDateField && endDateField.setDefaultDateTime) {
          endDateField.setDefaultDateTime();
        }
        this.$forceUpdate();
      });

      if (
        this.assetFormDetails.est_end_date &&
        this.assetFormDetails.est_end_time &&
        this.assetFormDetails.trip_date
      ) {

        if (!this.getListCalled) {
          this.getListCalled = true;
          this.getLists().catch((err) => {
            console.error("Error calling getLists:", err);
          });
        }
      }
    },

    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      this.resizeGrid();
    },

    resizeGrid() {
      if (this.gridOptions && "api" in this.gridOptions) {
        setTimeout(() => {
          this.gridOptions.api.sizeColumnsToFit();
        }, 100);
      }
    },

    setSelectedRows() {
      this.selectedLegs = this.gridApi.getSelectedRows();
      this.resetFormValidation();


      const legsCount = (this.selectedLegs.length || 0) + 1;


      this.assetFormDetails.buffer_time = 30 * legsCount;
    },

    partResetValues() {
      this.assetFormDetails.trailer_1 = null;
      this.assetFormDetails.trailer_2 = null;
      this.assetFormDetails.vehicle = null;
      this.assetFormDetails.driver = null;
      this.resetFormValidation();
    },

    checkAssetType() {
      if (this.assetFormDetails.trip_date) {
        this.getLists();
      }
    },

    resetFormValidation() {
      if (this.$refs.combineLegAssetForm) {
        this.$refs.combineLegAssetForm.resetValidation();
      }
    },

    resetValues() {
      this.assetFormDetails = {
        buffer_time: 30,
      };
      this.selectedTrip = null;
      this.allLegsList = [];
      this.selectedLegs = [];
      this.vehicleList = [];
      this.driverList = [];
      this.trailer1List = [];
      this.trailer2List = [];
      this.trip_date = null;
      this.selectedLegIds = [];
    },

    getAssetList(assetType) {
      const params = {
        asset_type: assetType,
        trip_date: this.assetFormDetails.trip_date,
        est_start_date: this.assetFormDetails.est_start_date,
        est_start_time: this.assetFormDetails.est_start_time,
        est_end_date: this.assetFormDetails.est_end_date,
        est_end_time: this.assetFormDetails.est_end_time,
      
      };

      if (this.currentProfile && this.currentProfile.company) {
        params.company = this.currentProfile.company;
      }

      if (this.assetFormDetails.id) {
        params.trip = this.assetFormDetails.id;
      }

      return this.$api.trip
        .getTripAssetList(this.legId, params)
        .then((res) => {
          if (assetType == "Trailer") {
            this.trailer1List = res.data.trailer1;
            this.trailer2List = res.data.trailer2;
          }
          if (assetType == "Vehicle") {
            this.vehicleList = res.data;
          }
          if (assetType == "Driver") {
            this.driverList = res.data;
          }
        })
        .catch((err) => {
          console.error(err);
        });
    },

    async getAllocatedAssets() {
      this.allListApi = [];

      if (this.selectedTrip) {
        await this.$api.trip
          .getAllocatedAssets({ leg: this.selectedTrip.leg_id })
          .then(async (res) => {
            this.assetFormDetails = { ...res.data };
            if (this.assetFormDetails.duration) {
              this.routeDuration = this.assetFormDetails.duration;
            }
            if (this.assetFormDetails.trip_date) {
              this.assetFormDetails.trip_date = res.data.trip_date.replace(
                " ",
                "T"
              );
            }
            this.assetFormDetails.milestone_template = res.data.milestone_template
            this.assetFormDetails.buffer_time = Number(this.assetFormDetails.buffer_time) || 30;

            if (this.assetFormDetails.trip_date) {
              this.getLists()
                .then(() => {
                  this.assetFormDetails.vehicle = res.data.vehicle;
                  this.assetFormDetails.trailer_1 = res.data.trailer_1;
                  this.assetFormDetails.trailer_2 = res.data.trailer_2;
                  this.assetFormDetails.driver = res.data.driver;
                })
                .catch((err) => {
                  console.error(err);
                });
            }
          })
          .catch((err) => {
            console.error(err);
          });
      }
    },

    getSimillarLegs(params = {}) {
      this.$bus.$emit("showLoader", true);

      params = { leg: this.legId };
      this.$api.trip
        .getSimillarLegs(params)
        .then((res) => {
          this.legDetails = res.data;
          this.allLegsList = [
            ...res.data.combined_legs,
            ...res.data.similar_legs,
          ];
          this.setSelectedValues(res.data.combined_legs);
          if (
            res.data &&
            res.data.current_leg &&
            res.data.current_leg.leg_trip
          ) {
            this.selectedTrip = {
              ...res.data.current_leg.leg_trip,
              leg_id: res.data.current_leg.id,
            };
            setTimeout(() => {
              this.getAllocatedAssets();
            }, 400);
          }
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },

    setSelectedValues(combineNodes = []) {
      const interval = setInterval(() => {
        if (this.gridApi) {
          clearInterval(interval);
          const selectedLegsIds = combineNodes.map((leg) => leg.id);
          this.gridApi.forEachNode((node) => {
            if (selectedLegsIds.indexOf(node.data.id) > -1) {
              node.setSelected(true);
            }
          });
        }
      }, 400);
    },

    submitForm() {
      this.formErrors = {};
      this.$bus.$emit("showLoader", true);
      const data = {
        current_leg: this.legDetails.current_leg.id,
        depot: this.legData.depot,
        combined_legs: this.selectedLegs.map((leg) => leg.id),
        trip: {
          ...this.assetFormDetails,
        },
      };

      this.$api.trip
        .combineLegs(data)
        .then((res) => {
          this.$bus.$emit("showLoader", false);
          this.$bus.$emit("showToastMessage", {
            message: "Leg Combined successfully!",
            color: "success",
          });
          this.resetFormValidation();
          this.showCombineLegsForm = false;
          this.$emit("refreshList");
        })
        .catch((err) => {
          this.$bus.$emit("showLoader", false);
          this.$bus.$emit("showToastMessage", {
            message: "Couldn't Combine legs!",
            color: "error",
          });
          this.formErrors = err.data;
        });
    },
  },
};
</script>

<style scoped>
.combine-legs-card {
  border-radius: 16px !important;
  overflow: hidden;
}

.combine-legs-header {
  background: linear-gradient(135deg, #f07025 0%, #f07025 100%) !important;
  border-radius: 0 !important;
  position: relative;
}

.combine-legs-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.1);
  pointer-events: none;
}

.header-content {
  position: relative;
  z-index: 1;
}

.title-section {
  display: flex;
  align-items: center;
}

.route-display {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.location-chip {
  transition: all 0.3s ease !important;
  font-weight: 500 !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
}

.location-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15) !important;
}

.route-arrow {
  margin: 0 4px;
  animation: pulse 2s infinite;
}

@keyframes pulse {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.7;
  }
}

.close-btn {
  transition: all 0.3s ease !important;
}

.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.1) !important;
  transform: rotate(90deg);
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.details-card {
  border-radius: 12px !important;
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

.details-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}

.detail-item {
  padding: 8px 0;
}

.detail-label {
  font-size: 0.75rem;
  color: #666;
  font-weight: 500;
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

.detail-value {
  font-size: 0.9rem;
  font-weight: 600;
  color: #333;
  word-break: break-word;
}

.search-field {
  transition: all 0.3s ease;
}

.search-field:hover {
  transform: translateY(-1px);
}

.grid-container {
  border-radius: 12px !important;
  overflow: hidden;
  border: 1px solid #e0e0e0;
}

.enhanced-grid {
  border-radius: 12px;
}

.enhanced-grid ::v-deep .ag-header {
  background: linear-gradient(135deg, #f5f5f5 0%, #eeeeee 100%);
  border-bottom: 2px solid #1976d2;
}

.enhanced-grid ::v-deep .ag-header-cell {
  border-right: 1px solid #ddd;
  font-weight: 600;
}

.enhanced-grid ::v-deep .ag-row {
  transition: all 0.2s ease;
}

.enhanced-grid ::v-deep .ag-row:hover {
  background-color: #f8f9ff !important;
  transform: translateX(2px);
}

.enhanced-grid ::v-deep .ag-row-selected {
  background-color: #e3f2fd !important;
  border-left: 4px solid #1976d2;
}

.enhanced-grid ::v-deep .ag-cell {
  display: flex;
  align-items: center;
  padding: 8px 12px;
}

.form-card {
  border-radius: 12px !important;
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

.form-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05) !important;
}

.form-field-wrapper {
  position: relative;
  transition: all 0.3s ease;
}

.form-field-wrapper:hover {
  transform: translateY(-1px);
}

.form-field-wrapper ::v-deep .v-input__control {
  transition: all 0.3s ease;
}

.form-field-wrapper ::v-deep .v-text-field--outlined fieldset {
  border-color: #ddd;
  transition: all 0.3s ease;
}

.form-field-wrapper ::v-deep .v-text-field--outlined:hover fieldset {
  border-color: #1976d2;
}

.form-field-wrapper ::v-deep .v-text-field--outlined.v-input--is-focused fieldset {
  border-color: #1976d2;
  border-width: 2px;
}

.error-alert {
  border-radius: 12px !important;
  margin-bottom: 16px;
}

.actions-section {
  background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%);
  border-top: 1px solid #e0e0e0;
}

.submit-btn {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%) !important;
  color: white !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 8px !important;
  padding: 0 24px !important;
  transition: all 0.3s ease !important;
  position: relative;
  overflow: hidden;
}

.submit-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  transition: left 0.5s;
}

.submit-btn:hover::before {
  left: 100%;
}

.submit-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(25, 118, 210, 0.3) !important;
}

.submit-btn:disabled {
  background: #ccc !important;
  transform: none !important;
  box-shadow: none !important;
}

/* Responsive Design */
@media (max-width: 960px) {
  .route-display {
    margin-top: 8px;
  }

  .location-chip {
    font-size: 0.7rem !important;
  }

  .grid-container {
    margin: 0 -8px;
  }
}

/* Animation for form field focus */
.form-field-wrapper ::v-deep .v-input--is-focused .v-input__prepend-inner {
  color: #1976d2 !important;
}

/* Custom scrollbar for dialog */
.combine-legs-card {
  max-height: 90vh;
  overflow-y: auto;
}

.combine-legs-card::-webkit-scrollbar {
  width: 8px;
}

.combine-legs-card::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.combine-legs-card::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.combine-legs-card::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>